import React from 'react';

import { FmButton } from './FmButton';
import { FmText } from './FmComposition';
import { FmModal } from './FmModal';
import {
  getReportWorkflowSlot,
  type FmReportWorkflowSlotDescriptor,
} from '@/shared/config/reportWorkflowSlotRegistry';

export interface FmReportMessageWorkflowDescriptor {
  kind: 'message';
  id: string;
  open: boolean;
  title: string;
  body?: string;
  confirmLabel: string;
  dismissible: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
  testId?: string;
}

/**
 * Controlled workflows opened by actions inside an owner report. These are
 * explicit sealed capabilities rather than route-composed modal JSX.
 */
export type FmReportWorkflowDescriptor =
  | FmReportMessageWorkflowDescriptor
  | FmReportWorkflowSlotDescriptor;

const ReportMessageWorkflow: React.FC<{
  workflow: FmReportMessageWorkflowDescriptor;
}> = ({ workflow }) => (
  <FmModal
    open={workflow.open}
    title={workflow.title}
    onCancel={workflow.onDismiss}
    dismissible={workflow.dismissible}
    data-testid={workflow.testId}
    footer={(
      <FmButton
        variant="primary"
        onClick={workflow.onConfirm}
        data-testid={workflow.testId ? `${workflow.testId}-ok` : undefined}
      >
        {workflow.confirmLabel}
      </FmButton>
    )}
  >
    {workflow.body ? <FmText>{workflow.body}</FmText> : null}
  </FmModal>
);

export const FmReportWorkflows: React.FC<{
  workflows: readonly FmReportWorkflowDescriptor[];
}> = ({ workflows }) => (
  <>
    {workflows.map((workflow) => {
      if (workflow.kind === 'message') {
        return <ReportMessageWorkflow key={workflow.id} workflow={workflow} />;
      }
      const Renderer = getReportWorkflowSlot(workflow.slotKey);
      return React.createElement(Renderer, {
        key: workflow.id,
        ...workflow.slotProps,
      });
    })}
  </>
);

FmReportWorkflows.displayName = 'FmReportWorkflows';
